import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Sticky buy buttons component that appears when the main buy buttons
 * scroll out of the viewport.
 *
 * Syncs price and media thumbnail with the main product section
 * using the theme's standard event patterns.
 *
 * @extends Component
 */
class StickyBuyButtonsComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {boolean} */
  #buyButtonsInView = true;

  /** @type {boolean} */
  #footerInView = false;

  /** @type {boolean} */
  #scrollThresholdPassed = false;

  /** @type {Element | null} */
  #section = null;

  /** Scroll threshold in pixels before enabling sticky bar */
  static #SCROLL_THRESHOLD = 100;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section');
    this.#abortController = new AbortController();
    this.#initBuyButtonsObserver();
    this.#initFooterObserver();
    this.#initScrollThreshold();
    this.#bindEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
    this.#buyButtonsObserver = null;
    this.#footerObserver?.disconnect();
    this.#footerObserver = null;
    this.#abortController?.abort();
    this.#abortController = null;
    this.#section = null;
  }

  /**
   * Initialize the IntersectionObserver to watch the main buy buttons element.
   */
  #initBuyButtonsObserver() {
    const section = this.#section
      || this.closest('[class*="1-product-information"]')
      || this.closest('[data-section-type="product"]');

    if (!section) return;

    const targetElement = section.querySelector('[class*="1-buy-buttons-block"]')
      || section.querySelector('.buy-buttons-block');

    if (!targetElement) return;

    this.#buyButtonsObserver = new IntersectionObserver(
      (entries) => {
        this.#buyButtonsInView = entries[0].isIntersecting;
        this.#updateVisibility();
      },
      { threshold: 0 }
    );

    this.#buyButtonsObserver.observe(targetElement);
  }

  /**
   * Initialize the IntersectionObserver to watch the footer element.
   */
  #initFooterObserver() {
    const footer = document.querySelector('footer')
      || document.querySelector('[class*="footer"]')
      || document.querySelector('#shopify-section-footer');

    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        this.#footerInView = entries[0].isIntersecting;
        this.#updateVisibility();
      },
      { threshold: 0 }
    );

    this.#footerObserver.observe(footer);
  }

  /**
   * Initialize scroll threshold detection.
   * Sticky bar won't show until user has scrolled past threshold.
   * Hides again if user scrolls back above threshold.
   */
  #initScrollThreshold() {
    const signal = this.#abortController?.signal;
    if (!signal) return;

    // Check initial scroll position
    this.#scrollThresholdPassed = window.scrollY >= StickyBuyButtonsComponent.#SCROLL_THRESHOLD;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const isPastThreshold = window.scrollY >= StickyBuyButtonsComponent.#SCROLL_THRESHOLD;
        if (isPastThreshold !== this.#scrollThresholdPassed) {
          this.#scrollThresholdPassed = isPastThreshold;
          this.#updateVisibility();
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
  }

  /**
   * Update sticky bar visibility based on all conditions.
   * Show when: scroll threshold passed AND buy buttons NOT visible AND footer NOT visible
   */
  #updateVisibility() {
    if (this.#scrollThresholdPassed && !this.#buyButtonsInView && !this.#footerInView) {
      this.#show();
    } else {
      this.#hide();
    }
  }

  /**
   * Bind to variant update events.
   */
  #bindEvents() {
    const signal = this.#abortController?.signal;
    if (!signal) return;

    const target = this.#section || document;
    target.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  /**
   * Handle variant update events.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { detail } = event;
    if (!detail?.resource) return;

    const { resource, data } = detail;

    // Sync price
    if (data?.html) {
      this.#updatePrice(data.html);
    }

    // Sync thumbnail
    if (resource.featured_media) {
      this.#updateThumbnail(resource.featured_media);
    }
  };

  /**
   * Update price display from event HTML or fallback to main page.
   * @param {Document} html
   */
  #updatePrice(html) {
    const stickyPrice = this.querySelector('product-price [ref="priceContainer"]');
    if (!stickyPrice) return;

    // Try event HTML first, then fallback to main page
    let newPrice = html.querySelector('product-price [ref="priceContainer"]')
      || html.querySelector('[ref="priceContainer"]');

    if (!newPrice && this.#section) {
      newPrice = this.#section.querySelector(
        'product-price:not(.sticky-buy-buttons product-price) [ref="priceContainer"]'
      );
    }

    if (newPrice && stickyPrice.innerHTML !== newPrice.innerHTML) {
      stickyPrice.innerHTML = newPrice.innerHTML;
    }
  }

  /**
   * Update thumbnail image when variant changes.
   * @param {{ id: number, alt?: string, preview_image?: { src: string } }} media
   */
  #updateThumbnail(media) {
    const img = /** @type {HTMLImageElement | null} */ (
      this.querySelector('.sticky-product-thumbnail img')
    );
    if (!img || !media?.preview_image?.src) return;

    const newMediaId = String(media.id);
    if (img.dataset.mediaId === newMediaId) return;

    const baseSrc = media.preview_image.src;
    const buildSrc = (/** @type {number} */ w) => {
      if (baseSrc.includes('width=')) return baseSrc.replace(/width=\d+/, `width=${w}`);
      return baseSrc + (baseSrc.includes('?') ? '&' : '?') + `width=${w}`;
    };

    img.srcset = [50, 100, 200, 400].map((w) => `${buildSrc(w)} ${w}w`).join(', ');
    img.src = buildSrc(400);
    img.dataset.mediaId = newMediaId;
    img.alt = media.alt || '';
  }

  /**
   * Show the sticky bar.
   */
  #show() {
    if (this.#isVisible) return;
    this.#isVisible = true;
    this.removeAttribute('hidden');
    this.classList.add('is-visible');
  }

  /**
   * Hide the sticky bar.
   */
  #hide() {
    if (!this.#isVisible) return;
    this.#isVisible = false;
    this.classList.remove('is-visible');
    this.setAttribute('hidden', '');
  }
}

if (!customElements.get('sticky-buy-buttons-component')) {
  customElements.define('sticky-buy-buttons-component', StickyBuyButtonsComponent);
}
